import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import { adminNews, adminUploads, formatError } from "../api";
import { Button, Card, Input, PageHeader, Spinner, Switch, Textarea } from "../ui";

const emptyDraft = {
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  cover_image: "",
  published: false,
  meta_title: "",
  meta_description: "",
};

export default function NewsEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [draft, setDraft] = useState(isNew ? emptyDraft : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminNews.get(id).then(setDraft).catch(() => setDraft(false));
  }, [id, isNew]);

  if (draft === false) return <p className="text-sm text-red-600">News post not found.</p>;
  if (!draft) return <Spinner />;

  const setField = (k) => (v) => setDraft({ ...draft, [k]: v });

  const save = async () => {
    if (!draft.title || draft.title.trim().length < 2) return toast.error("Title is required");
    setBusy(true);
    try {
      const res = isNew ? await adminNews.create(draft) : await adminNews.update(id, draft);
      toast.success(isNew ? "News created." : "News updated.");
      if (isNew) nav(`/admin/news/${res.id}`, { replace: true });
      else setDraft(res);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const onUploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await adminUploads.upload(f, "news_cover");
      setDraft({ ...draft, cover_image: r.url });
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-news-editor">
      <Link to="/admin/news" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] mb-4"><ArrowLeft className="h-3.5 w-3.5" /> All news</Link>
      <PageHeader
        title={isNew ? "New news post" : "Edit news post"}
        actions={<Button onClick={save} loading={busy} testId="news-save"><Save className="h-3.5 w-3.5" /> {isNew ? "Create" : "Save"}</Button>}
      />

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <Card className="p-6 space-y-4">
            <Input label="Title" value={draft.title} onChange={(e) => setField("title")(e.target.value)} testId="news-title" />
            <Input label="Slug (optional)" value={draft.slug || ""} onChange={(e) => setField("slug")(e.target.value)} hint="Auto-generated from title if empty." testId="news-slug" />
            <Textarea label="Excerpt" rows={2} value={draft.excerpt || ""} onChange={(e) => setField("excerpt")(e.target.value)} testId="news-excerpt" />
            <div>
              <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Body</span>
              <div data-color-mode="light" data-testid="news-body-editor">
                <MDEditor
                  value={draft.body_html || ""}
                  onChange={(v) => setField("body_html")(v || "")}
                  height={420}
                  preview="edit"
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">Markdown supported — use **bold**, _italics_, lists, links, headings.</p>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">SEO</h3>
            <Input label="Meta title" value={draft.meta_title || ""} onChange={(e) => setField("meta_title")(e.target.value)} testId="news-meta-title" />
            <Textarea label="Meta description" rows={3} value={draft.meta_description || ""} onChange={(e) => setField("meta_description")(e.target.value)} testId="news-meta-description" />
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))] mb-3">Status</h3>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{draft.published ? "Published" : "Draft"}</div>
                <div className="text-xs text-slate-500">Toggle to publish to the public site.</div>
              </div>
              <Switch checked={!!draft.published} onChange={setField("published")} testId="news-published-toggle" />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))] mb-3">Cover image</h3>
            {draft.cover_image && (
              <img src={draft.cover_image} alt="" className="w-full h-40 object-cover rounded-lg border border-slate-200 mb-3" />
            )}
            <Input value={draft.cover_image || ""} onChange={(e) => setField("cover_image")(e.target.value)} placeholder="/api/files/news_cover/..." testId="news-cover-url" />
            <label className="mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Uploading..." : "Upload cover"}
              <input type="file" hidden accept="image/*" onChange={onUploadCover} data-testid="news-cover-file" />
            </label>
          </Card>
        </div>
      </div>
    </div>
  );
}
